import { useState, useCallback } from 'react';

/**
 * Chips as a thin UI over per-column filters: a chip is "on" iff its column's
 * filter is exactly that single value, so the filter popovers and the chip row
 * can never disagree.
 *
 * columnFilters shape: { [colId]: Set<string> | {from,to} | {min,max} }
 * (chips only ever read/write the Set form; range filters pass through untouched)
 */
export function useSyncedStatusChips(initialFilters) {
  const [columnFilters, setColumnFilters] = useState(initialFilters);

  // Set a column filter to exactly one value; null clears it ("All").
  const setChip = useCallback((colId, value) => {
    setColumnFilters((cur) => {
      const next = { ...cur };
      if (value == null) delete next[colId];
      else next[colId] = new Set([value]);
      return next;
    });
  }, []);

  // The single selected value for a column, or null if none / multiple.
  const chipValue = useCallback(
    (colId) => {
      const fv = columnFilters[colId];
      return fv instanceof Set && fv.size === 1 ? [...fv][0] : null;
    },
    [columnFilters]
  );

  return { columnFilters, setColumnFilters, setChip, chipValue };
}
